import React, { Suspense, lazy } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { CircularProgress, Box } from '@mui/material';
import { MainLayout } from '@/components/layout/MainLayout';
import { NewVersionNotice } from '@/components/layout/NewVersionNotice';
import { PageErrorBoundary } from '@/components/layout/PageErrorBoundary';
import { ProtectedRoute } from '@/components/auth/ProtectedRoute';

// Auth pages (not lazy - needed immediately)
import LoginPage from '@/components/auth/LoginPage';
import MfaSetupPage from '@/components/auth/MfaSetup';

// Lazy-loaded module pages
const DashboardPage = lazy(() => import('@/pages/DashboardPage'));
const AuditUniverseListPage = lazy(() => import('@/pages/AuditUniverseListPage'));
const AuditUniverseDetailPage = lazy(() => import('@/pages/AuditUniverseDetailPage'));
const RiskModelPage = lazy(() => import('@/pages/RiskModelPage'));
const ConformityListPage = lazy(() => import('@/pages/ConformityListPage'));
const ConformityDetailPage = lazy(() => import('@/pages/ConformityDetailPage'));
const RegistersPage = lazy(() => import('@/pages/RegistersPage'));
const SnapshotsPage = lazy(() => import('@/pages/SnapshotsPage'));
const RiskMatrixPage = lazy(() => import('@/pages/RiskMatrixPage'));
const OverallRiskPage = lazy(() => import('@/pages/OverallRiskPage'));
const PlanningOverviewPage = lazy(() => import('@/pages/PlanningOverviewPage'));
const MultiYearPlanPage = lazy(() => import('@/pages/MultiYearPlanPage'));
const PlanningCockpitPage = lazy(() => import('@/pages/PlanningCockpitPage'));
const GanttPage = lazy(() => import('@/pages/GanttPage'));
const EngagementListPage = lazy(() => import('@/pages/EngagementListPage'));
const EngagementDetailPage = lazy(() => import('@/pages/EngagementDetailPage'));
const AuditFieldDetailPage = lazy(() => import('@/pages/AuditFieldDetailPage'));
const FindingListPage = lazy(() => import('@/pages/FindingListPage'));
const FindingDetailPage = lazy(() => import('@/pages/FindingDetailPage'));
const ReportListPage = lazy(() => import('@/pages/ReportListPage'));
const ReportEditorPage = lazy(() => import('@/pages/ReportEditorPage'));
const AnnualReportListPage = lazy(() => import('@/pages/AnnualReportListPage'));
const AnnualReportEditorPage = lazy(() => import('@/pages/AnnualReportEditorPage'));
const MeasureListPage = lazy(() => import('@/pages/MeasureListPage'));
const MeasureDetailPage = lazy(() => import('@/pages/MeasureDetailPage'));
const CharterPage = lazy(() => import('@/pages/CharterPage'));
const QAIPPage = lazy(() => import('@/pages/QAIPPage'));
const CompetencePage = lazy(() => import('@/pages/CompetencePage'));
const IndependencePage = lazy(() => import('@/pages/IndependencePage'));
const EthicsPage = lazy(() => import('@/pages/EthicsPage'));
const StrategyPage = lazy(() => import('@/pages/StrategyPage'));
const MethodsPage = lazy(() => import('@/pages/MethodsPage'));
const ResourcePlanPage = lazy(() => import('@/pages/ResourcePlanPage'));
const PerformancePage = lazy(() => import('@/pages/PerformancePage'));
const ApprovalPackagePage = lazy(() => import('@/pages/ApprovalPackagePage'));
const UsersPage = lazy(() => import('@/pages/UsersPage'));
const RolesPage = lazy(() => import('@/pages/RolesPage'));
const SettingsPage = lazy(() => import('@/pages/SettingsPage'));
const ProfilePage = lazy(() => import('@/modules/profile/ProfilePage'));
const AuditTrailPage = lazy(() => import('@/pages/AuditTrailPage'));
const DataProtectionPage = lazy(() => import('@/pages/DataProtectionPage'));
const BusinessProcessesPage = lazy(() => import('@/pages/BusinessProcessesPage'));

const SuspenseFallback: React.FC = () => (
  <Box
    sx={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: '60vh',
    }}
  >
    <CircularProgress />
  </Box>
);

const App: React.FC = () => {
  return (
    // Die äußere Fehlergrenze fängt, was die der Seite (MainLayout) nicht
    // erreicht — Anmeldung und Rahmen. Ohne sie bleibt bei einem Fehler die
    // ganze Oberfläche weiß (Testgrundlage 2.2, T00).
    <PageErrorBoundary>
      <NewVersionNotice />
      <Suspense fallback={<SuspenseFallback />}>
        <Routes>
          {/* Public routes */}
          <Route path="/login" element={<LoginPage />} />
          <Route path="/mfa-setup" element={<MfaSetupPage />} />

          {/* Protected routes with MainLayout */}
          <Route
            element={
              <ProtectedRoute>
                <MainLayout />
              </ProtectedRoute>
            }
          >
            <Route path="/" element={<DashboardPage />} />

            {/* Audit Universe */}
            <Route path="/audit-universe" element={<AuditUniverseListPage />} />
            <Route path="/audit-universe/:id" element={<AuditUniverseDetailPage />} />

            {/* Risk */}
            <Route path="/risk/models" element={<RiskModelPage />} />
            <Route path="/risk/matrix" element={<RiskMatrixPage />} />
            <Route path="/risk/overall" element={<OverallRiskPage />} />

            {/* Planning */}
            <Route path="/planning" element={<PlanningOverviewPage />} />
            <Route path="/planning/multi-year" element={<MultiYearPlanPage />} />
            <Route path="/planning/cockpit" element={<PlanningCockpitPage />} />
            <Route path="/planning/gantt" element={<GanttPage />} />

            {/* Engagements */}
            <Route path="/engagements" element={<EngagementListPage />} />
            <Route path="/engagements/:id" element={<EngagementDetailPage />} />
            <Route path="/engagements/:id/fields/:fieldId" element={<AuditFieldDetailPage />} />

            {/* Findings */}
            <Route path="/findings" element={<FindingListPage />} />
            <Route path="/findings/:id" element={<FindingDetailPage />} />

            {/* Reports */}
            <Route path="/reports" element={<ReportListPage />} />
            <Route path="/reports/:id/edit" element={<ReportEditorPage />} />
            <Route path="/annual-reports" element={<AnnualReportListPage />} />
            <Route path="/annual-reports/:id" element={<AnnualReportEditorPage />} />

            {/* Measures */}
            <Route path="/measures" element={<MeasureListPage />} />
            <Route path="/measures/:id" element={<MeasureDetailPage />} />

            {/* Governance */}
            <Route path="/conformity" element={<ConformityListPage />} />
            <Route path="/conformity/registers" element={<RegistersPage />} />
            <Route path="/conformity/snapshots" element={<SnapshotsPage />} />
            <Route path="/conformity/:id" element={<ConformityDetailPage />} />
            <Route path="/governance/charter" element={<CharterPage />} />
            <Route path="/governance/qaip" element={<QAIPPage />} />
            <Route path="/governance/competence" element={<CompetencePage />} />
            <Route path="/governance/independence" element={<IndependencePage />} />
            <Route path="/governance/ethics" element={<EthicsPage />} />
            <Route path="/governance/strategy" element={<StrategyPage />} />
            <Route path="/governance/methods" element={<MethodsPage />} />
            <Route path="/governance/resources" element={<ResourcePlanPage />} />
            <Route path="/governance/performance" element={<PerformancePage />} />
            <Route path="/conformity/approval" element={<ApprovalPackagePage />} />

            {/* Admin */}
            <Route path="/admin/users" element={<UsersPage />} />
            <Route path="/admin/roles" element={<RolesPage />} />
            <Route path="/admin/settings" element={<SettingsPage />} />
            {/* Das eigene Konto. Zwei Adressen auf dieselbe Seite, damit die
                beiden Menüpunkte je auf ihrem Reiter landen. */}
            <Route path="/profile" element={<ProfilePage />} />
            <Route path="/profile/:tab" element={<ProfilePage />} />
            <Route path="/admin/audit-trail" element={<AuditTrailPage />} />
            <Route path="/admin/data-protection" element={<DataProtectionPage />} />

            {/* Business Processes */}
            <Route path="/business-processes" element={<BusinessProcessesPage />} />
          </Route>

          {/* Catch-all redirect */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
    </PageErrorBoundary>
  );
};

export default App;
